import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { debounce } from 'lodash';

import CustomButton, { TonalButton } from 'components/CustomButton';
import { SearchTextField } from 'components/CustomFields';
import { useServices, useSnackbar } from '../../contexts';
import { getCompaniesWithDetails } from 'services';
import { capitalizeFirstLetterOfNWords } from 'utils';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: '#fff',
    boxShadow: "0px 0px 4px 0px #00000026",
    borderRadius: '4px',
  },
  header: {
    position: 'relative',
    padding: theme.spacing(7, 10),
    backgroundImage: 'url("https://assets.languify.in/images/gradientBannerBgTwo.png")',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    width: '100%',
    height: '360px',
    overflow: 'hidden',
    backgroundSize: '100% 100%',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '20px',
      background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
      pointerEvents: 'none',
      zIndex: 1,
    },
  },
  title: {
    borderBottom: '1px solid #8692A3',
    paddingBottom: '22px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  content: {
    padding: theme.spacing(5,10),
  },
  cards: {
    display: 'flex', gap: '20px', 
    flexWrap: 'wrap', marginTop: '8px'
  },
  cardRoot: {
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    overflow: 'hidden',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    width: '344px'
  },
  cardContent: {
    display: 'flex', gap: '20px',
    backgroundColor: theme.palette.shades['clr-offWhite'],
    padding: theme.spacing(4)
  },
  cardImgContainer: {
    width: '56px', height: '56px',
    borderRadius: '8px',
    border: '1px solid #CCD4DE',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor : '#FFFFFF'
  },
  footer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(6,10),
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
  },
  emptyStateCard: {
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    borderRadius: '4px',
    display: 'flex',
    width: '700px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(7),
    gap: theme.spacing(5)
  },
  searchField: {
    display: 'flex', justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: '100px'
  }
}));

// Skeleton card component to show while loading
const SkeletonCompanyCard = () => {
  const classes = useStyles();

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Box className={classes.cardImgContainer}>
          <Skeleton variant="rectangular" width={56} height={56} />
        </Box>
        <Box width="100%">
          <Skeleton variant="text" width="80%" height={24} />
          <Box display='flex' gap={1} mt={1}>
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
        </Box>
      </Box>
      <Box style={{padding: '16px'}}>
        <Skeleton variant="rectangular" width="100%" height={36} />
      </Box>
    </Box>
  );
};

const SkeletonCards = ({ count = 6 }) => {
  const classes = useStyles();
  
  return (
    <Box className={classes.cards}>
      {Array(count).fill().map((_, index) => (
        <SkeletonCompanyCard key={index} />
      ))}
    </Box>
  );
};

const EmptyState = ({onClick=()=>{}}) => {
  const classes = useStyles();

  return(
    <Box style={{ width: '100%', display: 'flex', justifyContent: 'center'}}>
      <Box className={classes.emptyStateCard}>
        <img src='https://assets.languify.in/images/challenge-empty-state.png' alt='empty state'/>
        <Typography variant='h4-medium' color='neutral.clr-500'>
          Didn't find the company you are looking for?
        </Typography>
        <Typography variant='h6-medium'>
          Create a personalized coding challenge based for the company's job description and your resume
        </Typography>
        <CustomButton
          variant='contained'
          color='primary'
          size='medium'
          style={{ width: '100%' }}
          endIcon={<ArrowForwardIcon/>}
          onClick={onClick}
        >
          Create interview
        </CustomButton>
      </Box>
    </Box>
  )
};

export const CompanyCard = ({ 
  subTopics=0, totalQuestions=0, name='', onClick=() => {} 
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.cardRoot}>
      <Box className={classes.cardContent}>
        <Box className={classes.cardImgContainer}>
          <img
            src={`https://assets.languify.in/images/${name.replace(/\s+/g, '_')}.png`}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://assets.languify.in/images/companyTag.png";
            }}
            width={56}
            alt="logo"
            sx={{backgroundColor : '#FFFFFF'}}
          />
        </Box>
        <Box>
          <Typography variant='h5-medium'>{capitalizeFirstLetterOfNWords(name)}</Typography>
          <Box display='flex' gap={1} mt={1}>
            <Typography variant='body02-bold'>Topics: {subTopics} |</Typography>
            <Typography variant='body02-bold'>Questions: {totalQuestions}</Typography>
          </Box>
        </Box>
      </Box>
      <Box style={{padding: '16px'}}>
        <TonalButton variant='outlined' color='primary' style={{ width: '100%' }}
          onClick={onClick}
        >
          Prepare
        </TonalButton>
      </Box>
    </Box>
  );
};

const CompanyContent = ({ title="Companies", companies=[], setSelected}) => {
  const classes = useStyles();

  return !companies.length ? null : (
    <Box>
      <Typography variant='h5-medium' color='primary.clr-400'>
        {title}
      </Typography>
      <Box className={classes.cards}>
      {
        companies.map((data, i)=> 
            <CompanyCard
              key={i}
              {...data}
              subTopics={data?.subTopics?.length}
              onClick={()=>setSelected(data)}
            /> 
          )
      }
      </Box>
    </Box>
  )
}

const Companies = ({ setSelected=()=>{}, handleBack=()=>{}, openPersonalized=()=>{} }) => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();

  const [loading, setLoading] = React.useState(true);
  const [companiesData, setCompaniesData] = React.useState({});
  const [maangCompaniesData, setMaangCompaniesData] = React.useState({});
  const [searchQuery, setSearchQuery] = React.useState('');

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        let filters = {};
        if(searchQuery.length){
          filters.name = searchQuery
        }

        const result = await service.callService(
            getCompaniesWithDetails, filters
        );

        if(searchQuery.length){
          setCompaniesData(result);
          setMaangCompaniesData([]);
          return;
        }

        const { maangCompanies, otherCompanies } = Object.entries(result).reduce(
          (acc, [key, value]) => {
            const isMaang = ["amazon", "apple", "google", "meta", "netflix"].some(
                (name) => key.toLowerCase().includes(name)
              );

            acc[isMaang ? "maangCompanies" : "otherCompanies"][key] = value;
            return acc;
          },
          { maangCompanies: {}, otherCompanies: {} }
        );
  
        setMaangCompaniesData(maangCompanies);
        setCompaniesData(otherCompanies);
        
      } catch (error) {
        console.log(error);
        snackbar.error("Uh Oh! Unable to get the companies details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [searchQuery]);

  const handleInput = debounce((e) => {
    setSearchQuery(e.target.value);
  }, 500);

  const renderContent = () => {
    if (loading) {
      return (
        <Box style={{display: 'flex', flexDirection: 'column', gap: '30px'}}>
          <Box>
            <Typography variant='h5-medium' color='primary.clr-400'>
              Prepare for MAANG
            </Typography>
            <SkeletonCards count={3} />
          </Box>
          <Box>
            <Typography variant='h5-medium' color='primary.clr-400'>
              {searchQuery.length ? "Search Results" : 'Other companies'}
            </Typography>
            <SkeletonCards count={3} />
          </Box>
        </Box>
      );
    }

    if (Object.keys(maangCompaniesData).length > 0 || Object.keys(companiesData).length > 0) {
      return (
        <Box style={{display: 'flex', flexDirection: 'column', gap: '30px'}}>
          <CompanyContent 
            title='Prepare for MAANG' 
            companies={Object.values(maangCompaniesData)}
            setSelected={setSelected}
          />
          <CompanyContent 
            title={searchQuery.length ? "Search Results" : 'Other companies'}
            companies={Object.values(companiesData)}
            setSelected={setSelected}
          />
        </Box>
      );
    }

    return <EmptyState onClick={openPersonalized} />;
  };

  const LogoBox = ({ src, alt, top, left , right , bottom }) => (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: '12px',
        width: 56,
        height: 56,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px solid #CCD4DE',
        position: 'absolute',
        top,
        left,
        right,
        bottom
      }}
    >
      <img src={src} alt={alt} width={46} />
    </Box>
  );
  

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <LogoBox src='https://assets.languify.in/images/amazon.png' alt='Amazon' top='109px' left='60px' />
        <LogoBox src='https://assets.languify.in/images/google.png' alt='Google' top='264px' left='213px' />
        <LogoBox src='https://assets.languify.in/images/infosys.png' alt='Meta' top='109px' right='60px' />
        <LogoBox src='https://assets.languify.in/images/tcs.png' alt='Apple' top='264px' right='213px' />

        <Box className={classes.title}>
          <ArrowBackIcon style={{ cursor: 'pointer', color: '#0062E1' }} onClick={handleBack}/>
          <Typography variant='h5-medium'>Companies</Typography>
        </Box>
        <Box className={classes.searchField}>
          <SearchTextField
            placeholder='Search for your dream company'
            style={{backgroundColor: '#fff', width: '480px'}}
            defaultValue={searchQuery}
            onChange={handleInput}
          />
        </Box>
      </Box>

      <Box className={classes.content}>
        {renderContent()}
      </Box>

      {
      (loading || Object.keys(maangCompaniesData).length > 0 || Object.keys(companiesData).length > 0) ?
        <Box className={classes.footer} mt={8}>
          <Box style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
            <Typography variant='h4-medium' color='neutral.clr-500'>
              Didn't find the company?
            </Typography>
            <Typography variant='h6-medium' color='neutral.clr-700'>
              Create a personalized DSA coding challenge with job description & resume.
            </Typography>
          </Box>
          <Box>
            <CustomButton variant='outlined' color='primary' size='medium' onClick={openPersonalized}>
              Create&nbsp;personalized&nbsp;challenge
            </CustomButton>
          </Box>
        </Box>
        : null
      }
    </Box>
  );
};

export default Companies;